import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import * as notificationApi from '@/modules/notifications/api/notification.api'

export const useNotificationStore = create(
  persist(
    (set, get) => ({
      notifications: [],
      unreadCount: 0,
      isLoading: false,

      fetchNotifications: async () => {
        set({ isLoading: true })
        try {
          const backendList = await notificationApi.getNotifications({
            limit: 50,
          })
          if (Array.isArray(backendList)) {
            // Merge with any local unsaved notifications
            const currentList = get().notifications
            const backendIds = new Set(
              backendList.map((item) => String(item.id)),
            )

            const localOnly = currentList.filter(
              (item) => !backendIds.has(String(item.id)),
            )

            const combined = [...backendList, ...localOnly]
            // Sort newest first
            combined.sort(
              (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
            )

            // Ensure only 1 unread comment_reminder per lead, and 1 unread message per sender
            const seenCommentLeads = new Set()
            const seenMessageSenders = new Set()
            const deduplicated = []

            for (const item of combined) {
              if (
                item.type === 'comment_reminder' &&
                item.data?.leadId &&
                !item.read
              ) {
                if (seenCommentLeads.has(item.data.leadId)) {
                  continue
                }
                seenCommentLeads.add(item.data.leadId)
              }

              if (
                item.type === 'message' &&
                item.data?.senderId &&
                !item.read
              ) {
                if (seenMessageSenders.has(item.data.senderId)) {
                  continue
                }
                seenMessageSenders.add(item.data.senderId)
              }

              deduplicated.push(item)
            }

            const unreadCount = deduplicated.filter((item) => !item.read).length

            set({
              notifications: deduplicated.slice(0, 100),
              unreadCount,
              isLoading: false,
            })
            return deduplicated
          }
        } catch (error) {
          console.error('[NotificationStore] fetchNotifications error:', error)
        } finally {
          set({ isLoading: false })
        }
        return get().notifications
      },

      addNotification: (notification) =>
        set((state) => {
          const id =
            notification.id ||
            `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

          const title = notification.title || 'Notification'
          const body = notification.body || ''
          const type = notification.type || 'general'
          const data = notification.data || {}
          const createdAt = notification.createdAt || new Date().toISOString()

          // If comment_reminder for the same lead already exists in unread state, update it in-place so only 1 notification is shown
          if (type === 'comment_reminder' && data.leadId) {
            const existingIndex = state.notifications.findIndex(
              (item) =>
                item.type === 'comment_reminder' &&
                item.data?.leadId === data.leadId &&
                !item.read,
            )

            if (existingIndex !== -1) {
              const updated = [...state.notifications]
              updated[existingIndex] = {
                ...updated[existingIndex],
                id: notification.id || updated[existingIndex].id,
                title,
                body,
                data,
                createdAt,
              }

              return {
                notifications: updated,
                unreadCount: updated.filter((item) => !item.read).length,
              }
            }
          }

          // If message notification from the same sender already exists in unread state, update it in-place
          if (type === 'message' && data.senderId) {
            const existingIndex = state.notifications.findIndex(
              (item) =>
                item.type === 'message' &&
                item.data?.senderId === data.senderId &&
                !item.read,
            )

            if (existingIndex !== -1) {
              const updated = [...state.notifications]
              updated[existingIndex] = {
                ...updated[existingIndex],
                id: notification.id || updated[existingIndex].id,
                title,
                body,
                data,
                createdAt,
              }

              return {
                notifications: updated,
                unreadCount: updated.filter((item) => !item.read).length,
              }
            }
          }

          // Deduplicate if notification with same ID or same title & body (or leadId) already exists within 30s
          const exists = state.notifications.some((item) => {
            if (item.id === id) return true

            // Deduplicate matching title and body
            if (item.title === title && item.body === body) {
              const prevTime = new Date(item.createdAt).getTime()
              const currTime = new Date(createdAt).getTime()
              if (
                isNaN(prevTime) ||
                isNaN(currTime) ||
                Math.abs(currTime - prevTime) < 30000
              ) {
                return true
              }
            }

            // Deduplicate matching entity data (e.g. same leadId and type)
            if (
              data.leadId &&
              item.data?.leadId === data.leadId &&
              item.type === type
            ) {
              return true
            }

            return false
          })

          if (exists) {
            return state
          }

          const newNotification = {
            id,
            title,
            body,
            type,
            data,
            read: Boolean(notification.read),
            createdAt,
          }

          const notifications = [newNotification, ...state.notifications]
          const unreadCount = notifications.filter((item) => !item.read).length

          return {
            notifications,
            unreadCount,
          }
        }),

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((notification) => ({
            ...notification,
            read: true,
          })),
          unreadCount: 0,
        }))

        // Sync with backend API
        notificationApi.markAllNotificationsAsRead().catch((err) => {
          console.error('[NotificationStore] markAllAsRead error:', err)
        })
      },

      markAsRead: (notificationId) => {
        set((state) => {
          const notification = state.notifications.find(
            (item) => item.id === notificationId,
          )

          if (!notification || notification.read) {
            return state
          }

          const notifications = state.notifications.map((item) =>
            item.id === notificationId ? { ...item, read: true } : item,
          )

          return {
            notifications,
            unreadCount: notifications.filter((item) => !item.read).length,
          }
        })

        // Sync with backend API if it's a valid backend ID (24 hex)
        if (
          typeof notificationId === 'string' &&
          /^[0-9a-fA-F]{24}$/.test(notificationId)
        ) {
          notificationApi
            .markNotificationsAsRead([notificationId])
            .catch((err) => {
              console.error('[NotificationStore] markAsRead error:', err)
            })
        }
      },

      deleteNotification: (notificationId) => {
        set((state) => {
          const notifications = state.notifications.filter(
            (item) => item.id !== notificationId,
          )

          return {
            notifications,
            unreadCount: notifications.filter((item) => !item.read).length,
          }
        })

        if (
          typeof notificationId === 'string' &&
          /^[0-9a-fA-F]{24}$/.test(notificationId)
        ) {
          notificationApi.deleteNotification(notificationId).catch((err) => {
            console.error('[NotificationStore] deleteNotification error:', err)
          })
        }
      },

      clearNotifications: () =>
        set({
          notifications: [],
          unreadCount: 0,
        }),
    }),
    {
      name: 'crm-notifications-storage',
      partialize: (state) => ({
        notifications: state.notifications.slice(0, 100),
        unreadCount: state.notifications
          .slice(0, 100)
          .filter((item) => !item.read).length,
      }),
    },
  ),
)
